"use client";

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileCheck, Mail, List } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const heroPattern = '/assets/marketing/hero-pattern-960w.webp';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'definitions', title: 'Definitions' },
  { id: 'scope-purpose', title: 'Scope & Purpose' },
  { id: 'processing-principles', title: 'Processing Principles' },
  { id: 'security-measures', title: 'Security Measures' },
  { id: 'sub-processors', title: 'Sub-processors' },
  { id: 'data-subject-rights', title: 'Data Subject Rights' },
  { id: 'international-transfers', title: 'International Transfers' },
  { id: 'contact', title: 'Contact' },
];

const definitions = [
  { term: 'Controller', def: 'The customer who determines the purposes and means of processing personal data.' },
  { term: 'Processor', def: 'Hirebee HRMS, which processes personal data on behalf of the Controller.' },
  { term: 'Personal Data', def: "Any information relating to an identified or identifiable natural person ('data subject')." },
  { term: 'Sub-processor', def: 'A third party engaged by Hirebee to process personal data on behalf of the Controller.' },
  { term: 'Data Breach', def: 'A breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to personal data.' },
  { term: 'Processing', def: 'Any operation performed on personal data, including collection, recording, storage, adaptation, retrieval, consultation, use, disclosure, erasure, or destruction.' },
];

export default function DPAClient() {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-100px 0px -80% 0px', threshold: 0 }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b" style={{ backgroundColor: '#E9FBFA' }}>
        <div className="hidden md:block absolute inset-0 opacity-60" style={{ backgroundImage: `url(${heroPattern})`, backgroundSize: 'cover' }} />
        <div className="container mx-auto relative z-10 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-[#195cc8] text-sm font-medium mb-6">
              <FileCheck className="w-4 h-4" />
              Data Processing
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Data Processing Agreement</h1>
            <p className="text-lg text-muted-foreground">GDPR-compliant data processing terms for enterprise customers</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[250px_1fr] gap-8">
            {/* Sidebar TOC */}
            <nav className="hidden lg:block">
              <div className="sticky top-32 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border rounded-lg p-4 max-h-[calc(100vh-160px)] overflow-y-auto">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
                  <List className="h-4 w-4" />
                  <span>On This Page</span>
                </div>
                <ul className="space-y-1 text-sm">
                  {sections.map(({ id, title }) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        onClick={(e) => scrollTo(e, id)}
                        className={cn(
                          'block py-1.5 px-2 rounded transition-colors hover:bg-muted',
                          activeId === id
                            ? 'text-primary font-medium border-l-2 border-primary pl-3'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            {/* Content */}
            <div className="space-y-8">
              <Card id="overview">
                <CardHeader>
                  <CardTitle>Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-muted-foreground">
                  <p>This Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of the Terms of Service between Hirebee HRMS (&ldquo;Processor&rdquo;) and the customer (&ldquo;Controller&rdquo;) who processes personal data using our services.</p>
                  <p>This DPA ensures compliance with applicable data protection laws, including the General Data Protection Regulation (EU) 2016/679 (&ldquo;GDPR&rdquo;), the UK GDPR, and other applicable privacy legislation. It establishes the obligations of each party regarding the processing of personal data.</p>
                  <p>This DPA applies automatically to all customers whose use of Hirebee involves the processing of personal data of individuals located in the European Economic Area (EEA), the United Kingdom, or Switzerland.</p>
                </CardContent>
              </Card>

              <Card id="definitions">
                <CardHeader>
                  <CardTitle>Definitions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {definitions.map(({ term, def }) => (
                      <div key={term}>
                        <h3 className="font-semibold mb-1">{term}</h3>
                        <p className="text-muted-foreground">{def}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card id="scope-purpose">
                <CardHeader>
                  <CardTitle>Scope &amp; Purpose</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-2">Categories of Data Processed</h3>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Employee personal information (name, email, phone number, address)</li>
                      <li>Employment data (job title, department, start date, salary information)</li>
                      <li>Leave and attendance records</li>
                      <li>Performance review data and feedback</li>
                      <li>Recruitment and applicant data</li>
                      <li>Training and development records</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Data Subjects</h3>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Current, former, and prospective employees of the Controller</li>
                      <li>Job applicants and candidates</li>
                      <li>Contractors and temporary workers</li>
                      <li>Emergency contacts and dependents (limited data)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Processing Activities</h3>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Storage and retrieval of employee records</li>
                      <li>Processing payroll and benefits administration</li>
                      <li>Managing leave requests and attendance tracking</li>
                      <li>Facilitating recruitment workflows and applicant tracking</li>
                      <li>Generating reports and analytics (aggregated/anonymized where possible)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card id="processing-principles">
                <CardHeader>
                  <CardTitle>Data Processing Principles</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Hirebee shall process personal data in accordance with the following principles:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Lawfulness:</strong> Process data only on documented instructions from the Controller</li>
                    <li><strong className="text-foreground">Purpose limitation:</strong> Process data only for the specified purposes outlined in this DPA</li>
                    <li><strong className="text-foreground">Data minimization:</strong> Ensure only necessary data is collected and processed</li>
                    <li><strong className="text-foreground">Accuracy:</strong> Maintain data accuracy and provide tools for the Controller to rectify data</li>
                    <li><strong className="text-foreground">Storage limitation:</strong> Retain data only for as long as necessary to fulfill processing purposes</li>
                    <li><strong className="text-foreground">Confidentiality:</strong> Ensure all personnel authorized to process data are bound by confidentiality obligations</li>
                  </ul>
                </CardContent>
              </Card>

              <Card id="security-measures">
                <CardHeader>
                  <CardTitle>Security Measures</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-2">Technical Safeguards</h3>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>End-to-end encryption for data in transit (TLS 1.2+) and at rest (AES-256)</li>
                      <li>Multi-factor authentication and role-based access controls</li>
                      <li>Regular security monitoring, vulnerability scanning, and penetration testing</li>
                      <li>Secure data centers with SOC 2 Type II compliance</li>
                      <li>Automated backups with encrypted off-site storage</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Organizational Measures</h3>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Employee privacy training and confidentiality agreements</li>
                      <li>Regular privacy impact assessments (DPIAs)</li>
                      <li>Third-party vendor management and due diligence</li>
                      <li>Designated Data Protection Officer (DPO)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Incident Response</h3>
                    <p className="text-muted-foreground">In the event of a personal data breach, Hirebee will notify the Controller without undue delay and no later than <strong className="text-foreground">72 hours</strong> after becoming aware of the breach. The notification will include the nature of the breach, categories and approximate number of data subjects affected, likely consequences, and measures taken or proposed to address the breach.</p>
                  </div>
                </CardContent>
              </Card>

              <Card id="sub-processors">
                <CardHeader>
                  <CardTitle>Sub-processors</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">Hirebee engages certain third-party sub-processors to assist in providing our services. We maintain a list of current sub-processors and will update the Controller prior to any changes.</p>
                  <div>
                    <h3 className="font-semibold mb-2">Notification &amp; Objection</h3>
                    <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>Hirebee will notify the Controller at least <strong className="text-foreground">30 days</strong> before engaging a new sub-processor</li>
                      <li>The Controller may object to the engagement of a new sub-processor within 14 days of notification</li>
                      <li>If a reasonable objection is raised, Hirebee will make reasonable efforts to provide an alternative or allow the Controller to terminate the affected services</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Due Diligence</h3>
                    <p className="text-muted-foreground">All sub-processors are subject to the same data protection obligations as set out in this DPA. Hirebee conducts regular audits of sub-processors to ensure ongoing compliance with security and privacy requirements.</p>
                  </div>
                </CardContent>
              </Card>

              <Card id="data-subject-rights">
                <CardHeader>
                  <CardTitle>Data Subject Rights</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">Hirebee will assist the Controller in fulfilling data subject requests under GDPR, including:</p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                    <li><strong className="text-foreground">Right of access</strong> &mdash; provide copies of personal data being processed</li>
                    <li><strong className="text-foreground">Right to rectification</strong> &mdash; correct inaccurate or incomplete data</li>
                    <li><strong className="text-foreground">Right to erasure</strong> &mdash; delete personal data (&ldquo;right to be forgotten&rdquo;)</li>
                    <li><strong className="text-foreground">Right to restrict processing</strong> &mdash; limit how data is processed</li>
                    <li><strong className="text-foreground">Right to data portability</strong> &mdash; export data in a structured, machine-readable format</li>
                    <li><strong className="text-foreground">Right to object</strong> &mdash; object to processing based on legitimate interests</li>
                    <li><strong className="text-foreground">Right to withdraw consent</strong> &mdash; withdraw previously given consent at any time</li>
                  </ul>
                  <p className="text-muted-foreground">Hirebee will respond to data subject requests within <strong className="text-foreground">30 days</strong> and will promptly notify the Controller of any direct requests received from data subjects.</p>
                </CardContent>
              </Card>

              <Card id="international-transfers">
                <CardHeader>
                  <CardTitle>International Transfers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">Data may be transferred to and processed in countries outside the EEA. Hirebee ensures adequate protection through the following mechanisms:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Standard Contractual Clauses (SCCs):</strong> We use the European Commission&apos;s approved SCCs (2021/914) for transfers to countries without an adequacy decision</li>
                    <li><strong className="text-foreground">Adequacy decisions:</strong> Where available, we rely on European Commission adequacy decisions recognizing equivalent data protection standards</li>
                    <li><strong className="text-foreground">Supplementary measures:</strong> We implement additional technical and organizational safeguards where required by transfer impact assessments</li>
                    <li><strong className="text-foreground">UK Addendum:</strong> For transfers from the UK, we apply the International Data Transfer Addendum to the EU SCCs as approved by the UK ICO</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Contact CTA */}
              <Card id="contact" className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle>Get the Full DPA</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Enterprise customers can request the complete Data Processing Agreement including detailed annexes and technical specifications. For questions about data processing practices, contact our legal team.
                  </p>
                  <Link
                    to="/contacts"
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Contact Us
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
